import { constants } from "ethers";
import { parseEther } from "ethers/lib/utils";
import { ChainId, NetworkName, TierName, TierRequiredAmount, Wallet } from "./enums";

export const USER_LOGIN_SIGNATURE_KEY = "Gagarin Launchpad sign confirmation" as const;

export const TWENTY_SECONDS = 20_000 as const;
export const TWO_MINUTES = 120_000 as const;
export const ONE_HOUR = 3_600_000 as const;
export const TWO_HOURS = 7_200_000 as const;

export const AddressZero = constants.AddressZero;
export const MaxUint256 = constants.MaxUint256;

export const ROOT_MAINNET_ADDRESSES_MAP = {
  [ChainId.BINANCE_MAINNET]: "0xdFDeC470fcC6E1ee7fBb7A0F486F9a755216B638",
  [ChainId.CSC_MAINNET]: "0xa83132B24836141985357A36EB81D61e5b04f5Db",
  [ChainId.OKC_MAINNET]: "0x3e9041CA8D0049d1Dc6cb00B6658AC104e93Ef4b",
  [ChainId.POLYGON_MAINNET]: "0xa83132B24836141985357A36EB81D61e5b04f5Db",
};
export const ROOT_TESTNET_ADDRESSES_MAP = {
  [ChainId.BINANCE_TESTNET]: "0x425Dcc09a33AfEa348617d177B3ac03fC511206e",
  [ChainId.CSC_TESTNET]: "0x01Ac070152856dc6DC6540de13777C8b75F2E1e9",
  [ChainId.OKC_TESTNET]: "0x01Ac070152856dc6DC6540de13777C8b75F2E1e9",
  [ChainId.POLYGON_TESTNET]: "0x6ABf6a8F888ef1A02415E11B453066A2B50aba49",
};

export const ROOT_ADDRESSES_MAP = {
  [ChainId.UNSET]: ROOT_MAINNET_ADDRESSES_MAP[ChainId.BINANCE_MAINNET],
  ...ROOT_MAINNET_ADDRESSES_MAP,
  ...ROOT_TESTNET_ADDRESSES_MAP,
};

export const TIERS_TOKENS_AMOUNT_MAP = {
  [TierName.TIER_1]: parseEther(TierRequiredAmount.TIER_1),
  [TierName.TIER_2]: parseEther(TierRequiredAmount.TIER_2),
  [TierName.TIER_3]: parseEther(TierRequiredAmount.TIER_3),
  [TierName.TIER_4]: parseEther(TierRequiredAmount.TIER_4),
};

export const NODES_MAP = {
  [ChainId.UNSET]: "",
  [ChainId.BINANCE_MAINNET]: "https://bsc-dataseed.binance.org/",
  [ChainId.BINANCE_TESTNET]: "https://data-seed-prebsc-1-s1.binance.org:8545",
  [ChainId.CSC_MAINNET]: "https://rpc.coinex.net",
  [ChainId.CSC_TESTNET]: "https://testnet-rpc.coinex.net/",
  [ChainId.OKC_MAINNET]: "https://exchainrpc.okex.org",
  [ChainId.OKC_TESTNET]: "https://exchaintestrpc.okex.org",
  [ChainId.POLYGON_MAINNET]: "https://polygon-rpc.com/",
  [ChainId.POLYGON_TESTNET]: "https://rpc-mumbai.maticvigil.com",
};

export const NETWORK_NAMES_MAP = {
  [ChainId.UNSET]: NetworkName.UNSET,
  [ChainId.BINANCE_MAINNET]: NetworkName.BINANCE_MAINNET,
  [ChainId.BINANCE_TESTNET]: NetworkName.BINANCE_TESTNET,
  [ChainId.CSC_MAINNET]: NetworkName.CSC_MAINNET,
  [ChainId.CSC_TESTNET]: NetworkName.CSC_TESTNET,
  [ChainId.OKC_MAINNET]: NetworkName.OKC_MAINNET,
  [ChainId.OKC_TESTNET]: NetworkName.OKC_TESTNET,
  [ChainId.POLYGON_MAINNET]: NetworkName.POLYGON_MAINNET,
  [ChainId.POLYGON_TESTNET]: NetworkName.POLYGON_TESTNET,
};

export const DEEPLINKS_MAP = {
  [Wallet.Metamask]: `https://metamask.app.link/dapp/${window.location.host}/`,
  [Wallet.TrustWallet]: `https://link.trustwallet.com/open_url?coin_id=20000714&url=${window.location.href}`,
  [Wallet.OKX]:
    "https://chrome.google.com/webstore/detail/okx-wallet/mcohilncbfahbmgdjkbpemcciiolgcge",
};

export const EXPLORERS_MAP = {
  [ChainId.UNSET]: {
    url: "https://bscscan.com/",
    name: "Bscscan",
  },
  [ChainId.BINANCE_MAINNET]: {
    url: "https://bscscan.com/",
    name: "Bscscan",
  },
  [ChainId.BINANCE_TESTNET]: {
    url: "https://testnet.bscscan.com/",
    name: "Bscscan Testnet",
  },
  [ChainId.CSC_MAINNET]: {
    url: "https://www.coinex.net/",
    name: "CSC Explorer",
  },
  [ChainId.CSC_TESTNET]: {
    url: "https://testnet.coinex.net/",
    name: "Testnet CSC Explorer",
  },
  [ChainId.OKC_MAINNET]: {
    url: "https://www.oklink.com/okc/",
    name: "OKLink",
  },
  [ChainId.OKC_TESTNET]: {
    url: "https://www.oklink.com/okc-test/",
    name: "OKLink",
  },
  [ChainId.POLYGON_MAINNET]: {
    url: "https://polygonscan.com/",
    name: "Polygon Exploror",
  },
  [ChainId.POLYGON_TESTNET]: {
    url: "https://mumbai.polygonscan.com/",
    name: "Mumbai Exploror",
  },
};

export const NETWORK_DATA_MAP = {
  [ChainId.UNSET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.UNSET],
    node: NODES_MAP[ChainId.UNSET],
    name: NETWORK_NAMES_MAP[ChainId.UNSET],
    explorer: EXPLORERS_MAP[ChainId.UNSET],
  },
  [ChainId.BINANCE_MAINNET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.BINANCE_MAINNET],
    node: NODES_MAP[ChainId.BINANCE_MAINNET],
    name: NETWORK_NAMES_MAP[ChainId.BINANCE_MAINNET],
    explorer: EXPLORERS_MAP[ChainId.BINANCE_MAINNET],
  },
  [ChainId.BINANCE_TESTNET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.BINANCE_TESTNET],
    node: NODES_MAP[ChainId.BINANCE_TESTNET],
    name: NETWORK_NAMES_MAP[ChainId.BINANCE_TESTNET],
    explorer: EXPLORERS_MAP[ChainId.BINANCE_TESTNET],
  },
  [ChainId.CSC_MAINNET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.CSC_MAINNET],
    node: NODES_MAP[ChainId.CSC_MAINNET],
    name: NETWORK_NAMES_MAP[ChainId.CSC_MAINNET],
    explorer: EXPLORERS_MAP[ChainId.CSC_MAINNET],
  },
  [ChainId.CSC_TESTNET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.CSC_TESTNET],
    node: NODES_MAP[ChainId.CSC_TESTNET],
    name: NETWORK_NAMES_MAP[ChainId.CSC_TESTNET],
    explorer: EXPLORERS_MAP[ChainId.CSC_TESTNET],
  },
  [ChainId.OKC_MAINNET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.OKC_MAINNET],
    node: NODES_MAP[ChainId.OKC_MAINNET],
    name: NETWORK_NAMES_MAP[ChainId.OKC_MAINNET],
    explorer: EXPLORERS_MAP[ChainId.OKC_MAINNET],
  },
  [ChainId.OKC_TESTNET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.OKC_TESTNET],
    node: NODES_MAP[ChainId.OKC_TESTNET],
    name: NETWORK_NAMES_MAP[ChainId.OKC_TESTNET],
    explorer: EXPLORERS_MAP[ChainId.OKC_TESTNET],
  },
  [ChainId.POLYGON_MAINNET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.POLYGON_MAINNET],
    node: NODES_MAP[ChainId.POLYGON_MAINNET],
    name: NETWORK_NAMES_MAP[ChainId.POLYGON_MAINNET],
    explorer: EXPLORERS_MAP[ChainId.POLYGON_MAINNET],
  },
  [ChainId.POLYGON_TESTNET]: {
    root: ROOT_ADDRESSES_MAP[ChainId.POLYGON_TESTNET],
    node: NODES_MAP[ChainId.POLYGON_TESTNET],
    name: NETWORK_NAMES_MAP[ChainId.POLYGON_TESTNET],
    explorer: EXPLORERS_MAP[ChainId.POLYGON_TESTNET],
  },
};

export const productionChainIds = [
  ChainId.BINANCE_MAINNET,
  ChainId.CSC_MAINNET,
  ChainId.OKC_MAINNET,
  ChainId.POLYGON_MAINNET,
];
export const developmentChainIds = [
  ChainId.BINANCE_TESTNET,
  ChainId.CSC_TESTNET,
  ChainId.OKC_TESTNET,
  ChainId.POLYGON_TESTNET,
];

export const allChainIds = [...developmentChainIds, ...productionChainIds];
